import type { GameState } from './types';
import { appendEvent } from './event-log';

export function cursedRelicTurnKey(state: GameState, playerId: string): string {
  return `${state.round}:${state.currentPlayerIndex}:${playerId}`;
}

export function cursedRelicAppliedTurnKey(state: GameState, victimId: string): string | undefined {
  return state.turnOrder[state.currentPlayerIndex] === victimId
    ? cursedRelicTurnKey(state, victimId)
    : undefined;
}

export function isCursedRelicLockedOut(state: GameState, playerId: string): boolean {
  const player = state.players.get(playerId);
  if (player == null || player.cursedRelicTurnsRemaining <= 0) return false;
  return state.activeCursedRelics.some(
    (relic) => relic.victimId === playerId && relic.turnsRemaining > 0,
  );
}

export function advanceCursedRelicLockoutAtTurnEnd(state: GameState, playerId: string): void {
  const player = state.players.get(playerId);
  const turnKey = cursedRelicTurnKey(state, playerId);
  let mirrorTurnsRemaining = 0;
  const updated = [];

  for (const relic of state.activeCursedRelics) {
    if (relic.victimId !== playerId) {
      updated.push(relic);
      continue;
    }

    if (relic.turnsRemaining <= 0) {
      continue;
    }

    if (relic.appliedTurnKey === turnKey) {
      const retained = { ...relic, appliedTurnKey: undefined };
      mirrorTurnsRemaining = Math.max(mirrorTurnsRemaining, retained.turnsRemaining);
      updated.push(retained);
      continue;
    }

    const turnsRemaining = relic.turnsRemaining - 1;
    if (turnsRemaining <= 0) {
      appendEvent(state, {
        type: 'item_dot_expired',
        playerId,
        victimId: playerId,
        itemId: 'cursed_relic',
      });
      continue;
    }

    mirrorTurnsRemaining = Math.max(mirrorTurnsRemaining, turnsRemaining);
    updated.push({ ...relic, turnsRemaining, appliedTurnKey: undefined });
  }

  state.activeCursedRelics = updated;
  if (player != null) player.cursedRelicTurnsRemaining = mirrorTurnsRemaining;
}

export function clearCursedRelicForPlayer(state: GameState, playerId: string): void {
  const player = state.players.get(playerId);
  const hadMirror = (player?.cursedRelicTurnsRemaining ?? 0) > 0;
  let hadActive = false;
  state.activeCursedRelics = state.activeCursedRelics.filter((relic) => {
    if (relic.victimId !== playerId) return true;
    hadActive ||= relic.turnsRemaining > 0;
    return false;
  });
  if (player != null) player.cursedRelicTurnsRemaining = 0;
  if (hadActive || hadMirror) {
    appendEvent(state, {
      type: 'item_dot_expired',
      playerId,
      victimId: playerId,
      itemId: 'cursed_relic',
    });
  }
}

export function scrubCursedRelicForRemovedPlayer(state: GameState, playerId: string): void {
  const affectedVictims = new Set<string>();
  state.activeCursedRelics = state.activeCursedRelics.filter((relic) => {
    const remove = relic.victimId === playerId || relic.casterId === playerId;
    if (remove) affectedVictims.add(relic.victimId);
    return !remove;
  });

  for (const victimId of affectedVictims) {
    const victim = state.players.get(victimId);
    if (victim != null) victim.cursedRelicTurnsRemaining = 0;
  }

  const removedPlayer = state.players.get(playerId);
  if (removedPlayer != null) removedPlayer.cursedRelicTurnsRemaining = 0;
}
